<template>
  <main>
    <div class="l-main-content__inner">
      <div class="c-page-header">
        <h1 class="c-page-header-title">宿題進捗一覧</h1>
        <div class="c-page-header__return">
          <a
            href="/teacher/homework/"
            class="c-button-white -return -teacher">
            <span class="label">戻る</span>
          </a>
        </div>
      </div>
      <template v-if="homework">
        <div class="c-page-section">
          <div class="p-homework-progress-title">
            <span
              class="c-parts-homework-status"
              :class="getHomeworkStatusColor(homework.status)"
            >{{ homework.status }}</span>
            <h2 class="p-homework-progress-title-text">{{ homework.name }}</h2>
          </div>
          <section class="p-homework-progress">
            <div class="p-homework-progress-detail-grid">
              <dl class="p-homework-progress-detail">
                <dt class="title">期間</dt>
                <dd class="content">{{ homework.start_datetime }}　〜　{{ homework.end_datetime }}</dd>
              </dl>
              <dl class="p-homework-progress-detail">
                <dt class="title">問題数</dt>
                <dd class="content">全{{ homework.questions_count }}問</dd>
              </dl>
              <dl class="p-homework-progress-detail">
                <dt class="title">達成条件</dt>
                <dd class="content">{{ homework.achievement_condition }}</dd>
              </dl>
              <dl class="p-homework-progress-detail">
                <dt class="title">再学習指示</dt>
                <dd class="content">{{ homework.relearning_required }}</dd>
              </dl>
            </div>
            <!-- この宿題について別画面で見るための導線 -->
            <div class="p-homework-progress-actions">
              <a :href="`/teacher/homework/${homework.id}/edit/`" target="_blank" class="c-button-white -prefix-icon -question-set">
                <span class="label">出題範囲を見る</span>
              </a>
              <!-- T9が改修されるまで一時的に非表示（FIXME: NFR-7613 で再表示する）
              <a href="/teacher/learning_progress/" target="_blank" class="c-button-white -prefix-icon -answer-status">
                <span class="label">問題ごとの正誤を見る</span>
              </a>
              -->
            </div>
          </section>
        </div>
        <div class="p-homework-search-form">
          <div class="p-homework-btn-list">
            <resettable-select-single
              :options="classroomList"
              v-model:selected="classroom"
              :init-label="true"
              :label-name="selectClassroomLabelName"
              :reset-option="true"
              @change="changeClassroom"
            ></resettable-select-single>

            <resettable-select-single
              :options="statusList"
              v-model:selected="status"
              :init-label="true"
              :label-name="selectStatusLabelName"
              :reset-option="true"
              @change="changeStatus"
            ></resettable-select-single>
            <ul class="p-homework-progress-status-list">
              <li>
                <span class="p-homework-progress-status -not-started">未着手</span>
                <span class="p-homework-progress-status-list__num">{{ homework.not_started_count }}人</span>
              </li>
              <li>
                <span class="p-homework-progress-status -in-progress">進行中</span>
                <span class="p-homework-progress-status-list__num">{{ homework.in_progress_count }}人</span>
              </li>
              <li>
                <span class="p-homework-progress-status -completion-within">完了(期限内)</span>
                <span class="p-homework-progress-status-list__num">{{ homework.completed_within_count }}人</span>
              </li>
              <li>
                <span class="p-homework-progress-status -completion-overdue">完了(期限後)</span>
                <span class="p-homework-progress-status-list__num">{{ homework.completed_after_count }}人</span>
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="hasHomeworkProgressItems"
          class="c-table-explain -right"
        >
          <copy-table-button :target-table="progressTableRef" />
        </div>
        <!-- sectionに.c-table-scroll-wrapをつけると、テーブル縦スクロールができる -->
        <section>
          <div class="c-table-scroll">
            <div class="c-table-scroll__inner">
              <table
                ref="progressTableRef"
                class="c-table"
              >
                <thead>
                  <tr>
                    <th>学級</th>
                    <th>出席番号</th>
                    <th>名前</th>
                    <th>
                      <div class="c-parts-tooltip-wrap">
                        進捗
                        <span class="c-parts-tooltip">説明
                          <span class="c-parts-tooltip__description -type2">
                            進捗が空欄の生徒は、宿題作成時に学級へ在籍していなかった生徒です。<br>
                            宿題設定の具体的な手順については
                            <a
                              href="https://manavi.wikipy.jp/teacher/p875"
                              target="_blank"
                            >コチラ</a>
                            を参照してください。
                          </span>
                        </span>
                      </div>
                    </th>
                    <th>学習済み問題数</th>
                    <th>宿題完了日</th>
                  </tr>
                </thead>
                <tbody v-if="hasHomeworkProgressItems">
                  <tr
                    v-for="progress in homework.homework_progress"
                    :key="progress.student_id"
                  >
                    <td class="u-text-center c-table-item-class">{{ progress.classroom_name }}</td>
                    <td class="u-text-center u-text-no-break">{{ progress.number }}</td>
                    <td>{{ progress.student_name }}</td>
                    <td class="u-text-center u-text-no-break">
                      <span
                        class="p-homework-progress-status -table"
                        :class="getProgressStatusColor(progress.status)"
                      >{{ progress.status_name }}</span>
                    </td>
                    <td class="u-text-center u-text-no-break">
                      <span
                        v-if="progress.studied_questions_count !== null"
                        class="p-homework-progress-bar"
                        :class="getProgressBarColor(progress.status)"
                      >
                        <span class="p-homework-progress-bar__count">{{ progress.studied_questions_count }}</span>
                        <progress
                          class="p-homework-progress-bar__gauge"
                          :max="homework.questions_count"
                          :value="progress.studied_questions_count"
                        ></progress>
                      </span>
                    </td>
                    <td class="u-text-center u-text-no-break">{{ progress.completed_at }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="6" class="c-table-no-data">該当の宿題進捗はありません。</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </template>
    </div>
    <error-modal></error-modal>
  </main>
</template>
<script setup lang="ts">
  import { ref, computed, onMounted } from "vue";
  import { HOMEWORK_STATUS, HOMEWORK_PROGRESS, HOMEWORK_PROGRESS_STATUS, SELECT_STATUS_LABEL_NAME, SELECT_CLASSROOM_LABEL_NAME } from "@/consts/homework";
  import { useHomeworkStore } from "@/store/teacher/homework_store";

  const homeworkStore = useHomeworkStore()

  const progressTableRef = ref<HTMLTableElement | null>(null)

  const homeworkId = ref(0)
  const homework = ref<any | null>(null)
  const statusList = ref(HOMEWORK_PROGRESS_STATUS)
  const status = ref<number| string>('')
  const selectStatusLabelName = ref(SELECT_STATUS_LABEL_NAME)
  const classroomList = ref([])
  const classroom = ref<string>('')
  const selectClassroomLabelName = ref(SELECT_CLASSROOM_LABEL_NAME)

  const hasHomeworkProgressItems = computed(() => !!homework.value?.homework_progress?.length)

  onMounted(async() => {

    setHomeworkIdFromPathParam()

    if (homeworkId.value) {
      homework.value  = await homeworkStore.fetchHomeworkProgress({homeworkId: homeworkId.value})
      // 学級取得API呼び出し
      let classrooms = await homeworkStore.fetchHomeworkClassrooms(homeworkId.value)
      // セレクトボックス選択肢用に組み替え
      classroomList.value = classrooms.classrooms.map(item => {
        return {'value': item.uuid, 'name': item.name}
      })
    } else {
      console.log("Invalid URL")
    }
  })

  // 進捗セレクトボックス変更時
  async function changeStatus() {
    homework.value  = await homeworkStore.fetchHomeworkProgress({homeworkId: homeworkId.value, status: status.value, classroom_uuids: classroom.value})
  }

  // 学級セレクトボックス変更時
  async function changeClassroom() {
    homework.value  = await homeworkStore.fetchHomeworkProgress({homeworkId: homeworkId.value, status: status.value, classroom_uuids: classroom.value})
  }

  function setHomeworkIdFromPathParam() {
    // パスパラメーターから宿題idを設定する
    const url = new URL(location.toString())
    const uri = url.pathname  // e.g. /teacher/homework/123/progress/
    const re = /^\/teacher\/homework\/(?<homeworkId>\d+)\/progress/
    const found = uri.match(re)
    if (found) {
      const parsed = parseInt(found.groups.homeworkId, 10)
      homeworkId.value = !isNaN(parsed) ? parsed : null
    }
  }

  function getHomeworkStatusColor(status: string) {
    // 宿題の状態に応じたCSSクラス名を返す
    switch (status) {
      case HOMEWORK_STATUS.UNPUBLISHED:
        return "-unpublished"
      case HOMEWORK_STATUS.PUBLISHED:
        return "-release"
      case HOMEWORK_STATUS.CLOSED:
        return "-end"
      default:
        return ""
    }
  }

  function getProgressStatusColor(status: number) {
    // 宿題進捗.状態に応じたCSSクラス名を返す
    switch (status) {
      case HOMEWORK_PROGRESS.NOTSTARTED:
        return "-not-started"
      case HOMEWORK_PROGRESS.INPROGRESS:
        return "-in-progress"
      case HOMEWORK_PROGRESS.COMPLETEDWITHIN:
        return "-completion-within"
      case HOMEWORK_PROGRESS.COMPLETEDAFTER:
        return "-completion-overdue"
      default:
        return ""
    }
  }

  function getProgressBarColor(status: number) {
    // 宿題進捗.状態に応じたCSSクラス名を返す
    switch (status) {
      case HOMEWORK_PROGRESS.NOTSTARTED:
        return "-not-started"
      case HOMEWORK_PROGRESS.INPROGRESS:
        return ""
      case HOMEWORK_PROGRESS.COMPLETEDWITHIN:
        return "-completed"
      case HOMEWORK_PROGRESS.COMPLETEDAFTER:
        return "-completed"
      default:
        return ""
    }
  }
</script>
